import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X } from "lucide-react";

const AdminLayout = ({ children, title, subtitle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const navItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: "📊" },
    { path: "/admin/reports", label: "Reports", icon: "⚠️" },
    { path: "/admin/users", label: "Users", icon: "👥" },
    { path: "/admin/stories", label: "Love Stories", icon: "💕" },
    { path: "/admin/verifications", label: "Verifications", icon: "✓" },
    { path: "/admin/promo", label: "Promo Codes", icon: "🎟️" },
    { path: "/admin/analytics", label: "Analytics", icon: "📈" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-800 border-b border-slate-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-white hover:bg-slate-700 rounded-lg"
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div>
              <span className="font-serif text-lg font-bold text-white">SingleSeen</span>
              <span className="text-rose-400 text-xs block">Admin</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed left-0 top-0 h-full w-64 bg-slate-800 border-r border-slate-700 p-6 z-50
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo - Hidden on mobile (shown in header) */}
        <div className="hidden lg:flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
            <span className="text-white text-lg">S</span>
          </div>
          <div>
            <span className="font-serif text-lg font-bold text-white block">SingleSeen</span>
            <span className="text-rose-400 text-xs">Admin Panel</span>
          </div>
        </div>

        {/* Mobile close button area */}
        <div className="lg:hidden h-16" />

        <nav className="space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                isActive(item.path)
                  ? "bg-rose-500/10 text-rose-400 font-medium"
                  : "text-slate-400 hover:bg-slate-700 hover:text-white"
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="text-sm">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-white text-sm"
          >
            ← Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Page Header */}
          {(title || subtitle) && (
            <div className="mb-6 lg:mb-8">
              {title && <h1 className="font-serif text-2xl lg:text-3xl text-white mb-1">{title}</h1>}
              {subtitle && <p className="text-slate-400 text-sm lg:text-base">{subtitle}</p>}
            </div>
          )}
          
          {/* Page Content */}
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
